import React, { useEffect, useMemo, useState } from "react";
import { BookOpen, Library, ShieldAlert, Microscope, Sparkles, Layers, Plus, Trash2, Search } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const TIER_ICON = {
  evidence_based: Microscope,
  historical: BookOpen,
  traditional: Library,
  emerging: Sparkles,
  experimental: ShieldAlert,
  user_owned: Layers,
};

const TIER_PILL_CLASS = {
  evidence_based: "mios-pill-normal",
  historical: "mios-pill-low",
  traditional: "mios-pill-low",
  emerging: "mios-pill-warning",
  experimental: "mios-pill-warning",
  user_owned: "mios-pill-low",
};

export default function KnowledgePage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [tab, setTab] = useState("library"); // library | ontology | evidence | import
  const [buckets, setBuckets] = useState({});
  const [ontologies, setOntologies] = useState([]);
  const [evidenceLevels, setEvidenceLevels] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [domain, setDomain] = useState("");
  const [error, setError] = useState("");
  const [userItems, setUserItems] = useState([]);

  const load = async () => {
    try {
      const [b, o, e, s, u] = await Promise.all([
        api.get("/knowledge/buckets", { params: { q: query || undefined, domain: domain || undefined } }),
        api.get("/knowledge/ontologies", { params: { language } }),
        api.get("/knowledge/evidence-levels", { params: { language } }),
        api.get("/knowledge/stats"),
        api.get("/knowledge/items", { params: { layer: "user" } }),
      ]);
      setBuckets(b.data || {});
      setOntologies(o.data || []);
      setEvidenceLevels(e.data || []);
      setStats(s.data || null);
      setUserItems(u.data || []);
    } catch (ex) {
      setError(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const domains = useMemo(() => {
    const set = new Set();
    Object.values(buckets).forEach((arr) => (arr || []).forEach((it) => set.add(it.domain)));
    return Array.from(set);
  }, [buckets]);

  return (
    <div className="space-y-6" data-testid="knowledge-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            {t.knowledge.eyebrow}
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
            {t.knowledge.title}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">{t.knowledge.sub}</p>
        </div>
        {stats && (
          <div className="grid grid-cols-4 gap-2 text-[11px]" data-testid="knowledge-stats">
            <StatBox label={t.knowledge.layers.bootstrap} value={stats.by_layer.bootstrap} />
            <StatBox label={t.knowledge.layers.demo} value={stats.by_layer.demo} />
            <StatBox label={t.knowledge.layers.user} value={stats.by_layer.user} />
            <StatBox label={t.knowledge.layers.external} value={stats.by_layer.external} />
          </div>
        )}
      </header>

      {error && (
        <div className="mios-card" data-testid="knowledge-error">
          <div className="mios-card-body text-sm text-red-700">{error}</div>
        </div>
      )}

      <Tabs current={tab} onChange={setTab} t={t} />

      {tab === "library" && (
        <Library_
          buckets={buckets}
          domains={domains}
          domain={domain}
          setDomain={setDomain}
          query={query}
          setQuery={setQuery}
          reload={load}
          language={language}
          t={t}
        />
      )}
      {tab === "ontology" && <Ontology ontologies={ontologies} t={t} />}
      {tab === "evidence" && <Evidence levels={evidenceLevels} />}
      {tab === "kbimport" && (
        <Import_
          evidenceLevels={evidenceLevels}
          userItems={userItems}
          reload={load}
          t={t}
          language={language}
          canImport={!!user}
        />
      )}
    </div>
  );
}

function Tabs({ current, onChange, t }) {
  const tabs = [
    { key: "library",  label: t.knowledge.tabs.library  },
    { key: "ontology", label: t.knowledge.tabs.ontology },
    { key: "evidence", label: t.knowledge.tabs.evidence },
    { key: "kbimport",   label: t.knowledge.tabs.import_  },
  ];
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm" data-testid="knowledge-tabs">
      {tabs.map((tb) => (
        <button
          key={tb.key}
          type="button"
          onClick={() => onChange(tb.key)}
          data-testid={`knowledge-tab-${tb.key}`}
          className={`rounded-md px-3 py-1.5 transition-colors ${
            current === tb.key ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
          }`}
        >
          {tb.label}
        </button>
      ))}
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 px-2.5 py-1.5">
      <div className="uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <div className="mono mt-0.5 text-base font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function Library_({ buckets, domains, domain, setDomain, query, setQuery, reload, language, t }) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative flex items-center">
          <Search className="absolute left-2.5 h-4 w-4 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder={t.knowledge.searchPlaceholder}
            className="mios-input pl-8"
            data-testid="knowledge-search-input"
          />
        </label>
        <select
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          data-testid="knowledge-domain-filter"
          className="mios-input"
        >
          <option value="">{t.knowledge.allDomains}</option>
          {domains.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={reload}
          data-testid="knowledge-refresh-btn"
          className="mios-btn mios-btn-secondary"
        >
          {t.knowledge.refresh}
        </button>
      </div>
      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
        {Object.entries(buckets).map(([tier, items]) => {
          const Icon = TIER_ICON[tier] || Layers;
          return (
            <div key={tier} className="mios-card" data-testid={`knowledge-bucket-${tier}`}>
              <div className="mios-card-header">
                <div className="mios-card-title flex items-center gap-2">
                  <Icon className="h-4 w-4 text-slate-400" />
                  {t.knowledge.tiers[tier]}
                </div>
                <span className={`mios-pill ${TIER_PILL_CLASS[tier]} mono`}>{(items || []).length}</span>
              </div>
              <div className="mios-card-body space-y-2.5">
                {(items || []).length === 0 ? (
                  <div className="text-sm text-slate-400">{t.knowledge.emptyBucket}</div>
                ) : (
                  (items || []).map((it) => <KnowledgeRow key={it.id} item={it} language={language} t={t} />)
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function KnowledgeRow({ item, language, t }) {
  const title = language === "tr" ? item.title_tr : item.title_en;
  const abstract = language === "tr" ? item.abstract_tr : item.abstract_en;
  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid={`knowledge-row-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-slate-900">{title}</div>
        <span className="mios-pill mios-pill-low mono whitespace-nowrap">
          {t.knowledge.evidenceShort[item.evidence_level] || item.evidence_level}
        </span>
      </div>
      {abstract && <p className="mt-1 text-xs text-slate-600">{abstract}</p>}
      <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px] text-slate-500">
        <span className="rounded bg-slate-100 px-1.5 py-0.5">{item.domain}</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5">{item.region}</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5">{item.publication_date}</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 mono">{item.source_type}</span>
      </div>
    </div>
  );
}

function Ontology({ ontologies, t }) {
  return (
    <section className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3" data-testid="knowledge-ontology">
      {ontologies.map((o) => (
        <div key={o.key} className="mios-card">
          <div className="mios-card-header">
            <div className="mios-card-title">{o.label}</div>
            <span className="mios-pill mios-pill-low mono">{o.size}</span>
          </div>
          <div className="mios-card-body">
            <ul className="flex flex-wrap gap-1.5">
              {o.items.map((it) => (
                <li key={it.key} className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-700">
                  {it.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </section>
  );
}

function Evidence({ levels }) {
  return (
    <section className="space-y-2" data-testid="knowledge-evidence">
      {levels.map((lv) => (
        <div key={lv.key} className="mios-card">
          <div className="mios-card-body flex items-start gap-3">
            <span
              className="mt-1 inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: lv.color }}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-slate-900">{lv.label}</span>
                <span className="mios-pill mios-pill-low mono">{lv.tier}</span>
                <span className="mios-pill mios-pill-low mono">{lv.tone}</span>
                <span className="ml-auto text-[11px] text-slate-500">
                  ≈ {Math.round((lv.default_reliability || 0) * 100)}%
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{lv.description}</p>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}

function Import_({ evidenceLevels, userItems, reload, t, language, canImport }) {
  const [form, setForm] = useState({
    title: "",
    abstract: "",
    evidence_level: "user_supplied",
    domain: "general",
    region: "",
    source_type: "user_import",
    publication_date: new Date().toISOString().slice(0, 10),
    source_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!canImport || !form.title.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/knowledge/items", form);
      setForm({ ...form, title: "", abstract: "", source_url: "" });
      reload();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }
  async function remove(id) {
    try {
      await api.delete(`/knowledge/items/${id}`);
      reload();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section className="grid gap-4 lg:grid-cols-[420px,1fr]" data-testid="knowledge-import">
      <div className="mios-card">
        <div className="mios-card-header">
          <div className="mios-card-title flex items-center gap-2">
            <Plus className="h-4 w-4 text-slate-400" />
            {t.knowledge.addItem}
          </div>
        </div>
        <div className="mios-card-body space-y-3">
          <Field label={t.knowledge.fields.title} value={form.title} onChange={set("title")} testid="kf-title" />
          <Field label={t.knowledge.fields.abstract} value={form.abstract} onChange={set("abstract")} testid="kf-abstract" multiline />
          <div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
              {t.knowledge.fields.evidence_level}
            </div>
            <select value={form.evidence_level} onChange={set("evidence_level")} data-testid="kf-evidence" className="mios-input mt-1">
              {evidenceLevels.map((lv) => (
                <option key={lv.key} value={lv.key}>{lv.label}</option>
              ))}
            </select>
          </div>
          <Field label={t.knowledge.fields.domain} value={form.domain} onChange={set("domain")} testid="kf-domain" />
          <Field label={t.knowledge.fields.region} value={form.region} onChange={set("region")} testid="kf-region" />
          <Field label={t.knowledge.fields.source_type} value={form.source_type} onChange={set("source_type")} testid="kf-source-type" />
          <Field label={t.knowledge.fields.publication_date} value={form.publication_date} onChange={set("publication_date")} testid="kf-pubdate" />
          <Field label={t.knowledge.fields.source_url} value={form.source_url} onChange={set("source_url")} testid="kf-url" />
          {error && <div className="text-sm text-red-700">{error}</div>}
          <button
            type="button"
            disabled={busy || !form.title.trim()}
            onClick={submit}
            data-testid="kf-submit-btn"
            className="mios-btn mios-btn-primary w-full"
          >
            <Plus className="h-4 w-4" />
            {busy ? t.common.loading : t.knowledge.submit}
          </button>
          <p className="text-[11px] text-slate-500">{t.knowledge.copyrightWarning}</p>
        </div>
      </div>
      <div className="mios-card" data-testid="knowledge-user-items">
        <div className="mios-card-header">
          <div className="mios-card-title">{t.knowledge.myItems}</div>
          <span className="text-[11px] text-slate-400">{userItems.length}</span>
        </div>
        <div className="mios-card-body space-y-2.5">
          {userItems.length === 0 && <div className="text-sm text-slate-400">{t.knowledge.noUserItems}</div>}
          {userItems.map((it) => (
            <div key={it.id} className="flex items-start justify-between gap-2 rounded-md border border-slate-200 p-3">
              <div className="flex-1">
                <KnowledgeRow item={it} language={language} t={t} />
              </div>
              <button
                type="button"
                onClick={() => remove(it.id)}
                data-testid={`kf-delete-${it.id}`}
                className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                title={t.common.remove || "Sil"}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testid, multiline = false }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
      {multiline ? (
        <textarea data-testid={testid} value={value || ""} onChange={onChange} className="mios-input mt-1 min-h-[64px]" />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={onChange} className="mios-input mt-1" />
      )}
    </div>
  );
}
